import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, ExternalLink, FileSearch, Loader2, Search } from "lucide-react";
import { api, ApiError, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { AnalystReport, FirmSuggestion } from "../../../../shared/index";

const LIST_QUERY = ["analyst-reports", "list"] as const;
const ALL_FIRMS_QUERY = ["analyst-reports", "all-firms"] as const;
const ALL_FIRMS_STALE_MS = 24 * 60 * 60_000;
const LIMIT = 3;
const DEBOUNCE_MS = 200;

interface TickerPick {
  ticker: string;
  companyName: string;
}

export default function AnalystReportsPage() {
  const [tickerPick, setTickerPickRaw] = useState<TickerPick | null>(null);
  const [firmPick, setFirmPick] = useState<FirmSuggestion | null>(null);

  // Changing the ticker clears the firm pick.
  const setTickerPick = (t: TickerPick | null) => {
    setTickerPickRaw(t);
    setFirmPick(null);
  };

  const [submitted, setSubmitted] = useState<{
    ticker: string | null;
    firmId: number | null;
  } | null>(null);
  const [preview, setPreview] = useState<AnalystReport | null>(null);

  const allFirmsQuery = useQuery({
    queryKey: ALL_FIRMS_QUERY,
    queryFn: () => api.analystReports.firms(),
    staleTime: ALL_FIRMS_STALE_MS,
    gcTime: ALL_FIRMS_STALE_MS,
  });

  const query = useQuery({
    queryKey: [...LIST_QUERY, submitted],
    queryFn: () =>
      api.analystReports.list({
        ticker: submitted?.ticker ?? undefined,
        firmId: submitted?.firmId ?? undefined,
        limit: LIMIT,
      }),
    enabled: submitted !== null,
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!tickerPick || !firmPick) return;
    setSubmitted({
      ticker: tickerPick.ticker,
      firmId: firmPick.firmId,
    });
  };

  const reports = query.data?.reports ?? [];
  const canSearch = tickerPick !== null && firmPick !== null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Analyst Reports</h1>
        <p className="text-sm text-muted-foreground">
          Search the latest analyst report PDFs by ticker and/or firm.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-end"
      >
        <div className="flex-1 space-y-2">
          <Label htmlFor="ticker">Ticker</Label>
          <TickerCombobox value={tickerPick} onChange={setTickerPick} />
        </div>
        <div className="flex-1 space-y-2">
          <Label htmlFor="firm">Firm</Label>
          <FirmCombobox
            ticker={tickerPick?.ticker ?? null}
            allFirms={allFirmsQuery.data?.firms ?? []}
            loadingFirms={allFirmsQuery.isFetching}
            value={firmPick}
            onChange={setFirmPick}
          />
        </div>
        <Button type="submit" disabled={query.isFetching || !canSearch}>
          {query.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
          Search
        </Button>
      </form>

      {query.isError && (
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          {query.error instanceof ApiError
            ? query.error.message
            : "Failed to load reports"}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="border-b px-6 py-4">
          <h2 className="font-semibold">
            Reports{" "}
            {submitted && !query.isLoading && `(${reports.length})`}
          </h2>
        </div>

        {!submitted ? (
          <div className="flex flex-col items-center gap-2 p-12 text-center text-sm text-muted-foreground">
            <FileSearch className="h-8 w-8" />
            Pick a ticker and a firm, then hit Search.
          </div>
        ) : query.isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : reports.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            No reports found for that search.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticker</TableHead>
                <TableHead>Stock ID</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Report</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reports.map((r, i) => (
                <TableRow key={`${r.stockId}-${r.firmId}-${i}`}>
                  <TableCell className="font-mono text-sm font-semibold">
                    {r.ticker ?? (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {r.stockId}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.firmName ?? (
                      <span className="text-muted-foreground">
                        Firm {r.firmId}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(r.addedOn)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPreview(r)}
                        title="Preview in dialog"
                      >
                        <Eye className="h-3.5 w-3.5" /> Preview
                      </Button>
                      <Button asChild variant="ghost" size="sm" title="Download">
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PdfPreviewDialog
        report={preview}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      />
    </div>
  );
}

// ── Ticker autocomplete ────────────────────────────────────────

interface AutocompleteHit {
  label: string;
  value: string;
  category: string;
  extraData?: { market?: string };
}

function TickerCombobox({
  value,
  onChange,
}: {
  value: TickerPick | null;
  onChange: (v: TickerPick | null) => void;
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, DEBOUNCE_MS);

  const search = useQuery({
    queryKey: ["ticker-autocomplete", debounced],
    queryFn: () => fetchTickerSuggestions(debounced),
    enabled: debounced.trim().length >= 1,
    staleTime: 60_000,
  });

  const options = useMemo<ComboboxOption<TickerPick>[]>(() => {
    const hits = search.data ?? [];
    return hits
      .filter((h) => h.category === "ticker" && h.value)
      .map((h) => ({
        key: h.value,
        value: { ticker: h.value, companyName: h.label },
        label: (
          <div className="flex items-baseline justify-between gap-3">
            <span className="font-mono text-sm font-semibold">{h.value}</span>
            <span className="truncate text-xs text-muted-foreground">
              {h.label}
              {h.extraData?.market ? ` · ${h.extraData.market}` : ""}
            </span>
          </div>
        ),
      }));
  }, [search.data]);

  return (
    <Combobox<TickerPick>
      id="ticker"
      value={value}
      displayValue={value ? `${value.ticker} — ${value.companyName}` : ""}
      placeholder="e.g., AAPL"
      inputClassName="font-mono"
      loading={search.isFetching}
      options={options}
      onQueryChange={setQuery}
      onSelect={(v) => onChange(v)}
      emptyMessage={
        debounced.trim().length < 1 ? "Start typing…" : "No matches"
      }
    />
  );
}

async function fetchTickerSuggestions(q: string): Promise<AutocompleteHit[]> {
  const url = new URL("https://autocomplete.tipranks.com/api/autocomplete/search");
  url.searchParams.set("name", q);
  url.searchParams.set("onlyStockTickers", "true");
  url.searchParams.set("country", "1"); // US-only
  url.searchParams.set("topAnalysts", "0");
  url.searchParams.set("topInsiders", "0");
  url.searchParams.set("topBloggers", "0");
  url.searchParams.set("topTickers", "10");
  url.searchParams.set("topInstitutions", "0");
  const res = await fetch(url.toString());
  if (!res.ok) throw new Error(`Autocomplete failed: ${res.status}`);
  const data = (await res.json()) as AutocompleteHit[];
  return Array.isArray(data) ? data : [];
}

// ── Firm autocomplete (full list once a ticker is picked) ───

function FirmCombobox({
  ticker,
  allFirms,
  loadingFirms,
  value,
  onChange,
}: {
  ticker: string | null;
  allFirms: FirmSuggestion[];
  loadingFirms: boolean;
  value: FirmSuggestion | null;
  onChange: (v: FirmSuggestion | null) => void;
}) {
  const [query, setQuery] = useState("");

  // Client-side filter — no debounce, no network roundtrip per keystroke.
  const options = useMemo<ComboboxOption<FirmSuggestion>[]>(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? allFirms.filter((f) => f.firmName.toLowerCase().includes(q))
      : allFirms;
    return filtered.slice(0, 50).map((f) => ({
      key: String(f.firmId),
      value: f,
      label: (
        <div className="flex items-baseline justify-between gap-3">
          <span className="truncate">{f.firmName}</span>
          <span className="font-mono text-xs text-muted-foreground">
            {f.firmId}
          </span>
        </div>
      ),
    }));
  }, [allFirms, query]);

  const disabled = ticker === null;

  return (
    <Combobox<FirmSuggestion>
      id="firm"
      value={value}
      displayValue={value?.firmName ?? ""}
      placeholder={disabled ? "Pick a ticker first" : "Click to see firms"}
      loading={loadingFirms}
      disabled={disabled}
      options={options}
      minChars={0}
      onQueryChange={setQuery}
      onSelect={(v) => onChange(v)}
      emptyMessage={loadingFirms ? "Loading…" : "No matches"}
    />
  );
}

// ── helpers ────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  const t = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => setV(value), ms);
    return () => {
      if (t.current) clearTimeout(t.current);
    };
  }, [value, ms]);
  return v;
}

function PdfPreviewDialog({
  report,
  onOpenChange,
}: {
  report: AnalystReport | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={report !== null} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] w-[95vw] max-w-[95vw] flex-col gap-3 p-4 sm:max-w-[95vw]">
        <div className="flex items-start justify-between gap-4 pr-8">
          <div className="min-w-0 flex-1">
            <DialogTitle className="truncate text-base">
              {report?.ticker ?? `Stock ${report?.stockId ?? ""}`}
              {report?.firmName ? ` — ${report.firmName}` : ""}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {report ? formatDate(report.addedOn) : ""}
            </DialogDescription>
          </div>
          {report && (
            <Button asChild variant="outline" size="sm">
              <a
                href={buildProxyUrl(report.url)}
                target="_blank"
                rel="noreferrer noopener"
              >
                Open in new tab <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
        </div>
        {report && (
          <iframe
            key={report.url}
            src={buildProxyUrl(report.url)}
            title="Analyst report PDF"
            className="h-full w-full flex-1 rounded-md border bg-muted"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function buildProxyUrl(blobUrl: string): string {
  const token = getToken();
  const qs = new URLSearchParams({ url: blobUrl });
  if (token) qs.set("t", token);
  return `/api/analyst-reports/pdf?${qs.toString()}`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
